// Mobile Navigation Controller
// Handles mobile menu toggle functionality and flyout integration

class MobileNavigation {
  constructor() {
    this.menuToggle = document.querySelector('.mobile-menu-toggle');
    this.navMenu = document.querySelector('.nav-menu');
    this.isMenuOpen = false;
    this.isFlyoutOpen = false;
    this.currentFlyout = null;
    this.flyoutOverlay = null;
    this.mutationObserver = null; // Track observer instance
    this.breadcrumbClickHandler = null; // Track delegated event handler
    this.breadcrumbContainer = null; // Track container for cleanup

    this.init();
  }

  init() {
    // Always set up breadcrumb flyout handling (independent of mobile menu)
    // Listen for breadcrumb ready events from UserNavigation
    document.addEventListener('breadcrumbsReady', (event) => {
      console.log('📡 Mobile navigation received breadcrumbsReady event:', event.detail);
      this.setupMobileFlyoutTriggers();
    });

    // Check if breadcrumbs already exist (in case event fired before listener attached)
    const existingBreadcrumbs = document.querySelectorAll('.breadcrumb__item[data-flyout-key]');
    if (existingBreadcrumbs.length > 0) {
      console.log('📋 Breadcrumbs already exist, running initial setup');
      this.setupMobileFlyoutTriggers();
    }

    // Set up mobile menu toggle (if menu exists on this page)
    if (this.menuToggle && this.navMenu) {
      this.menuToggle.addEventListener('click', this.toggleMenu.bind(this));

      // Close menu when clicking nav links
      const navLinks = this.navMenu.querySelectorAll('.nav-link');
      navLinks.forEach(link => {
        link.addEventListener('click', this.closeMenu.bind(this));
      });

      // Close menu when clicking outside
      document.addEventListener('click', this.handleOutsideClick.bind(this));

      // Handle escape key
      document.addEventListener('keydown', this.handleKeydown.bind(this));

      // Handle resize
      window.addEventListener('resize', this.handleResize.bind(this));
    } else {
      console.log('ℹ️ Mobile menu toggle not found on this page');
    }

    // Initialize mobile flyout functionality
    this.initializeMobileFlyouts();
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    this.updateMenuState();
  }

  openMenu() {
    this.isMenuOpen = true;
    this.updateMenuState();
  }

  closeMenu() {
    this.isMenuOpen = false;
    this.updateMenuState();
  }

  updateMenuState() {
    if (this.isMenuOpen) {
      this.navMenu.classList.add('nav-menu-open');
      this.menuToggle.setAttribute('aria-expanded', 'true');
      this.menuToggle.classList.add('menu-open');

      // Animate hamburger to X
      const lines = this.menuToggle.querySelectorAll('.hamburger-line');
      if (lines.length >= 3) {
        lines[0].style.transform = 'rotate(45deg) translate(6px, 6px)';
        lines[1].style.opacity = '0';
        lines[2].style.transform = 'rotate(-45deg) translate(6px, -6px)';
      }
    } else {
      this.navMenu.classList.remove('nav-menu-open');
      this.menuToggle.setAttribute('aria-expanded', 'false');
      this.menuToggle.classList.remove('menu-open');

      // Reset hamburger animation
      const lines = this.menuToggle.querySelectorAll('.hamburger-line');
      if (lines.length >= 3) {
        lines[0].style.transform = '';
        lines[1].style.opacity = '';
        lines[2].style.transform = '';
      }
    }
  }

  handleOutsideClick(event) {
    // Handle mobile flyout clicks
    if (this.isFlyoutOpen && this.currentFlyout) {
      const flyoutContainer = this.currentFlyout.flyout;
      const trigger = this.currentFlyout.trigger;

      if (!flyoutContainer.contains(event.target) &&
        !trigger.contains(event.target)) {
        this.closeMobileFlyout();
        return;
      }
    }

    // Original menu handling
    if (this.isMenuOpen &&
      !this.navMenu.contains(event.target) &&
      !this.menuToggle.contains(event.target)) {
      this.closeMenu();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      if (this.isFlyoutOpen) {
        // Close flyout first
        this.closeMobileFlyout();
        return;
      }

      if (this.isMenuOpen) {
        this.closeMenu();
        this.menuToggle.focus();
      }
    }

    // Handle mobile flyout keyboard navigation
    if (this.isFlyoutOpen && this.currentFlyout) {
      this.handleMobileFlyoutKeyboard(event);
    }
  }

  /**
   * Check if current viewport matches mobile breakpoint using CSS media queries
   * @returns {boolean} True if mobile viewport
   */
  isMobileViewport() {
    // Use CSS media query that matches the SCSS mobile-only mixin
    // SCSS: @media (max-width: #{map.get($breakpoints, md) - 1px}) = max-width: 767px
    return window.matchMedia('(max-width: 767px)').matches;
  }

  handleResize() {
    // Close mobile menu if screen becomes large
    if (!this.isMobileViewport() && this.isMenuOpen) {
      this.closeMenu();
    }

    // Close mobile flyouts if screen becomes large
    if (!this.isMobileViewport() && this.isFlyoutOpen) {
      this.closeMobileFlyout();
    }
  }

  /**
   * Initialize mobile-specific flyout functionality
   */
  initializeMobileFlyouts() {
    // Create flyout overlay element
    this.createFlyoutOverlay();

    // NOTE: setupMobileFlyoutTriggers() is called by:
    // 1. Constructor fallback check (if breadcrumbs already exist)
    // 2. breadcrumbsReady event listener (when UserNavigation signals ready)
    // This ensures proper timing without duplicate setup

    // Handle device orientation changes
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        if (this.isFlyoutOpen) {
          this.repositionMobileFlyout();
        }
      }, 100);
    });
  }

  /**
   * Create the mobile flyout overlay
   */
  createFlyoutOverlay() {
    this.flyoutOverlay = document.createElement('div');
    this.flyoutOverlay.className = 'breadcrumb__flyout-overlay';
    this.flyoutOverlay.setAttribute('aria-hidden', 'true');

    // Add click handler to close flyout when clicking overlay
    this.flyoutOverlay.addEventListener('click', () => {
      this.closeMobileFlyout();
    });

    document.body.appendChild(this.flyoutOverlay);
  }

  /**
   * Set up mobile flyout triggers using event delegation
   */
  setupMobileFlyoutTriggers() {
    // Use same selector as UserNavigation: class OR id
    const breadcrumbContainer = document.querySelector('.breadcrumb') || document.getElementById('breadcrumb-container');

    if (!breadcrumbContainer) {
      console.log('⚠️ Breadcrumb container not found, skipping mobile trigger setup');
      return;
    }

    console.log('✅ Found breadcrumb container:', breadcrumbContainer.className || breadcrumbContainer.id);

    // Remove old delegated event listener if it exists
    if (this.breadcrumbClickHandler && this.breadcrumbContainer) {
      this.breadcrumbContainer.removeEventListener('click', this.breadcrumbClickHandler, true);
      console.log('🧹 Removed old breadcrumb click handler');
    }

    // Store container reference for cleanup
    this.breadcrumbContainer = breadcrumbContainer;

    // Create new delegated event handler
    this.breadcrumbClickHandler = (e) => {
      // Only handle clicks on mobile viewports
      if (!this.isMobileViewport()) {
        console.log('🖥️ Desktop viewport, allowing default navigation');
        return;
      }

      // Find the closest breadcrumb link that was clicked
      const link = e.target.closest('.breadcrumb__link');
      if (!link) {
        console.log('❌ No breadcrumb link found in click target');
        return;
      }

      // Find the breadcrumb item
      const item = link.closest('.breadcrumb__item');
      if (!item) {
        console.log('❌ No breadcrumb item found');
        return;
      }

      if (!item.hasAttribute('data-has-flyout')) {
        console.log('ℹ️ Breadcrumb item has no flyout, allowing navigation');
        return;
      }

      // Prevent default navigation and show flyout
      console.log('📱 Breadcrumb clicked on mobile:', link.textContent.trim());
      console.log('   Has flyout:', item.getAttribute('data-has-flyout'));
      console.log('   Flyout key:', item.getAttribute('data-flyout-key'));
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation(); // Prevent other listeners from firing
      this.showMobileFlyout(item, link);
    };

    // Attach single delegated event listener in CAPTURE phase to intercept before other handlers
    breadcrumbContainer.addEventListener('click', this.breadcrumbClickHandler, true);

    const breadcrumbItems = breadcrumbContainer.querySelectorAll('.breadcrumb__item[data-has-flyout="true"]');
    console.log('✅ Mobile flyout triggers set up via event delegation');
    console.log(`   Found ${breadcrumbItems.length} items with flyouts`);
    console.log('   Mobile viewport:', this.isMobileViewport());

    // Set up MutationObserver to watch for breadcrumb changes
    if (!this.mutationObserver) {
      console.log('📡 Creating MutationObserver for breadcrumb changes');

      let observerTimeout;
      this.mutationObserver = new MutationObserver((mutations) => {
        // Only react to changes in the breadcrumb container
        const hasBreadcrumbChange = mutations.some(mutation => {
          return mutation.target.classList?.contains('breadcrumb') ||
            mutation.target.closest?.('.breadcrumb') ||
            Array.from(mutation.addedNodes).some(node =>
              node.classList?.contains('breadcrumb__item') ||
              node.querySelector?.('.breadcrumb__item')
            );
        });

        if (hasBreadcrumbChange) {
          clearTimeout(observerTimeout);
          observerTimeout = setTimeout(() => {
            console.log('📱 MutationObserver detected breadcrumb change');
            // Event delegation handles new items automatically, just log for debugging
            const items = breadcrumbContainer.querySelectorAll('.breadcrumb__item[data-has-flyout="true"]');
            console.log(`   Updated: ${items.length} items with flyouts`);
          }, 50);
        }
      });

      this.mutationObserver.observe(breadcrumbContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-has-flyout']
      });
      console.log('✅ MutationObserver watching breadcrumb container');
    }
  }

  /**
   * Show mobile flyout
   * @param {HTMLElement} item - Breadcrumb item element
   * @param {HTMLElement} trigger - Trigger element
   */
  showMobileFlyout(item, trigger) {
    console.log('🚀 showMobileFlyout called for:', item.textContent.trim());

    // Close any existing flyout
    this.closeMobileFlyout();

    // Get flyout type from the link's data-flyout attribute
    const link = item.querySelector('.breadcrumb__link');
    const flyoutType = link ? link.getAttribute('data-flyout') : null;

    console.log('Flyout type determined:', flyoutType);

    if (!flyoutType) {
      console.log('No flyout type found, cannot show mobile flyout');
      return;
    }

    // Get flyout content from UserNavigation if available
    if (window.userNavigation && typeof window.userNavigation.getFlyoutContent === 'function') {
      console.log('Getting flyout content from UserNavigation...');
      const flyoutContent = window.userNavigation.getFlyoutContent(flyoutType);

      if (!flyoutContent || !flyoutContent.sections || !flyoutContent.sections.length) {
        console.log('No flyout content available for type:', flyoutType);
        return;
      }

      console.log('Creating mobile flyout with content:', flyoutContent);

      // Create mobile flyout
      const flyout = this.createMobileFlyoutElement(flyoutContent);
      document.body.appendChild(flyout);

      // Show overlay
      this.flyoutOverlay.classList.add('is-visible');

      // Show flyout
      requestAnimationFrame(() => {
        flyout.classList.add('is-visible');
      });

      // Track current flyout
      this.currentFlyout = {
        flyout: flyout,
        trigger: trigger,
        item: item
      };
      this.isFlyoutOpen = true;

      // Prevent body scroll
      this.preventBodyScroll(true);

      // Focus management
      this.focusFirstFlyoutItem(flyout);
    }
  }

  /**
   * Create mobile flyout element
   * @param {Object} flyoutContent - Flyout configuration object
   * @returns {HTMLElement} Mobile flyout element
   */
  createMobileFlyoutElement(flyoutContent) {
    const flyout = document.createElement('div');
    flyout.className = 'breadcrumb__flyout breadcrumb__flyout--mobile';
    flyout.setAttribute('role', 'dialog');
    flyout.setAttribute('aria-modal', 'true');
    flyout.setAttribute('aria-label', flyoutContent.title);

    let html = `
      <div class="breadcrumb__flyout-header">
        <h2 class="breadcrumb__flyout-title">${flyoutContent.title}</h2>
        <button class="breadcrumb__flyout-close" type="button" aria-label="Close menu">
          <span class="breadcrumb__flyout-close-icon">✕</span>
        </button>
      </div>
    `;

    if (flyoutContent.description) {
      html += `<div class="breadcrumb__flyout-description">${flyoutContent.description}</div>`;
    }

    flyoutContent.sections.forEach(section => {
      html += `<div class="breadcrumb__flyout-section">`;

      if (section.title) {
        html += `<h3 class="breadcrumb__flyout-section-title">${section.title}</h3>`;
      }

      html += '<ul class="breadcrumb__flyout-list" role="none">';

      section.items.forEach(item => {
        const iconHtml = item.icon ? `<span class="breadcrumb__flyout-icon" aria-hidden="true">${item.icon}</span>` : '';
        const href = item.action ? '#' : item.href;
        const actionAttr = item.action ? `data-action="${item.action}"` : '';

        html += `
          <li class="breadcrumb__flyout-item" role="none">
            <a href="${href}"
               class="breadcrumb__flyout-link"
               role="menuitem"
               ${actionAttr}
               ${item.description ? `title="${item.description}"` : ''}>
              ${iconHtml}
              <span class="breadcrumb__flyout-text">${item.label}</span>
            </a>
          </li>
        `;
      });

      html += '</ul></div>';
    });

    flyout.innerHTML = html;

    // Add close button handler
    const closeButton = flyout.querySelector('.breadcrumb__flyout-close');
    closeButton.addEventListener('click', () => {
      this.closeMobileFlyout();
    });

    // Add click handlers for action items
    flyout.addEventListener('click', (e) => {
      const link = e.target.closest('.breadcrumb__flyout-link');
      if (link) {
        if (link.hasAttribute('data-action')) {
          e.preventDefault();
          const action = link.getAttribute('data-action');

          // Call action handler from UserNavigation if available
          if (window.userNavigation && typeof window.userNavigation.handleFlyoutAction === 'function') {
            window.userNavigation.handleFlyoutAction(action, e);
          }
        }

        // Close flyout after any interaction
        setTimeout(() => {
          this.closeMobileFlyout();
        }, 100);
      }
    });

    return flyout;
  }

  /**
   * Close mobile flyout
   */
  closeMobileFlyout() {
    if (!this.isFlyoutOpen || !this.currentFlyout) return;

    const { flyout, trigger } = this.currentFlyout;

    // Hide flyout
    flyout.classList.remove('is-visible');
    this.flyoutOverlay.classList.remove('is-visible');

    // Remove after transition
    setTimeout(() => {
      if (flyout.parentNode) {
        flyout.remove();
      }
    }, 300);

    // Restore body scroll
    this.preventBodyScroll(false);

    // Return focus to trigger
    trigger.focus();

    // Reset state
    this.currentFlyout = null;
    this.isFlyoutOpen = false;
  }

  /**
   * Handle keyboard navigation in mobile flyout
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleMobileFlyoutKeyboard(event) {
    if (!this.currentFlyout) return;

    const { flyout } = this.currentFlyout;
    const focusableElements = flyout.querySelectorAll(
      'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    switch (event.key) {
      case 'Tab':
        // Trap focus within flyout
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.focusNextMobileFlyoutItem();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousMobileFlyoutItem();
        break;
    }
  }

  /**
   * Focus first item in mobile flyout
   * @param {HTMLElement} flyout - Flyout element
   */
  focusFirstFlyoutItem(flyout) {
    const firstLink = flyout.querySelector('.breadcrumb__flyout-link');
    if (firstLink) {
      firstLink.focus();
    }
  }

  /**
   * Focus next item in mobile flyout
   */
  focusNextMobileFlyoutItem() {
    if (!this.currentFlyout) return;

    const links = this.currentFlyout.flyout.querySelectorAll('.breadcrumb__flyout-link');
    const currentIndex = Array.from(links).indexOf(document.activeElement);
    const nextIndex = (currentIndex + 1) % links.length;
    links[nextIndex].focus();
  }

  /**
   * Focus previous item in mobile flyout
   */
  focusPreviousMobileFlyoutItem() {
    if (!this.currentFlyout) return;

    const links = this.currentFlyout.flyout.querySelectorAll('.breadcrumb__flyout-link');
    const currentIndex = Array.from(links).indexOf(document.activeElement);
    const previousIndex = currentIndex <= 0 ? links.length - 1 : currentIndex - 1;
    links[previousIndex].focus();
  }

  /**
   * Reposition mobile flyout (e.g., after orientation change)
   * Uses CSS custom properties to reduce viewport calculations during mobile keyboard events
   */
  repositionMobileFlyout() {
    if (!this.currentFlyout) return;

    // Mobile flyouts are centered, so just ensure they fit
    const { flyout } = this.currentFlyout;

    // Use CSS custom property for consistent max height, updated less frequently
    // This prevents expensive calculations during mobile keyboard show/hide
    const viewportHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--viewport-height')) || window.innerHeight;
    const maxHeight = viewportHeight * 0.8;

    flyout.style.maxHeight = `${maxHeight}px`;
  }

  /**
   * Prevent/restore body scroll
   * @param {boolean} prevent - Whether to prevent scroll
   */
  preventBodyScroll(prevent) {
    if (prevent) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    // Remove delegated breadcrumb click handler
    if (this.breadcrumbClickHandler && this.breadcrumbContainer) {
      this.breadcrumbContainer.removeEventListener('click', this.breadcrumbClickHandler, true);
      this.breadcrumbClickHandler = null;
      this.breadcrumbContainer = null;
      console.log('🧹 Removed breadcrumb click handler');
    }

    // Disconnect mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    // Close any open flyouts
    if (this.isFlyoutOpen) {
      this.closeMobileFlyout();
    }

    // Remove flyout overlay
    if (this.flyoutOverlay && this.flyoutOverlay.parentNode) {
      this.flyoutOverlay.parentNode.removeChild(this.flyoutOverlay);
      this.flyoutOverlay = null;
    }

    console.log('🧹 MobileNavigation destroyed and cleaned up');
  }
}

// Export the class for manual initialization from main.js
// Note: Auto-initialization removed to prevent duplicate instances
// MobileNavigation is now initialized in main.js for proper lifecycle management
export default MobileNavigation;
